import { DateFormats, formatDate, sortDateDesc } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { Grid, SecondaryData } from '@strata/react-grid';
import { MessagingBlock } from '@strata/react-messaging-block';
import { PageTitle } from '@strata/react-page-title';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { formatInboundOrtfSourceLabel } from './formatInboundOrtfSourceLabel';
import { OrtfInboundFileOverviewContent } from './OrtfInboundFileOverviewContent';

import {
    useGetCompletedInboundFilesQuery,
    useGetInboundFileOverviewQuery,
    useGetInboundFilesQuery,
} from 'store/api/ndpApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';

const INBOUND_FILES_EMPTY_MESSAGE = 'No inbound files processed.';

const inboundFileColumns = [
    {
        accessorKey: 'sendingPharmacyName',
        header: 'Sending Pharmacy',
        size: 200,
    },
    {
        accessorKey: 'prescriptions',
        header: 'Prescriptions',
        size: 120,
    },
    {
        accessorKey: 'fileName',
        header: 'File Name',
        size: 220,
    },
    {
        accessorKey: 'dateReceived',
        header: 'Received Date',
        size: 140,
        cell: ({ getValue }) => {
            const dateReceived = getValue();
            return dateReceived ? (
                <SecondaryData data={formatDate(dateReceived, DateFormats.DATE)} />
            ) : undefined;
        },
    },
];

const inboundFileSortDefaults = [{ id: 'dateReceived', desc: true }];

const mapCompletedInboundFileRow = (row) => ({
    ...row,
    prescriptions: row.scriptCount,
});

export const OrtfInboundFilesGridSection = ({ className = undefined, onRowClick }) => {
    const { sort, updateGridSort } = useGridState(
        Grids.ORTF_INBOUND_FILES,
        inboundFileSortDefaults,
    );
    const { data: completedInboundFiles = [], isFetching: isFetchingFiles } =
        useGetCompletedInboundFilesQuery();

    const displayFiles = completedInboundFiles.map(mapCompletedInboundFileRow);

    return (
        <div className={className}>
            <PageTitle title="Inbound Files" />
            <div className="margin__top--medium sds_ortf-outbound-grid">
                <Grid
                    columns={inboundFileColumns}
                    data={displayFiles}
                    enableSorting
                    isLoading={isFetchingFiles}
                    onRowClick={onRowClick}
                    onSort={updateGridSort}
                    sortProperties={sort}
                    noResults={
                        <div className="padding__around--large">
                            <MessagingBlock
                                size={MessagingBlock.Sizes.MEDIUM}
                                secondaryText={INBOUND_FILES_EMPTY_MESSAGE}
                            />
                        </div>
                    }
                />
            </div>
        </div>
    );
};

OrtfInboundFilesGridSection.propTypes = {
    className: PropTypes.string,
    onRowClick: PropTypes.func.isRequired,
};

export const OrtfInboundFileDetailsView = ({
    selectedInboundFile,
    onBack,
    backLabel = 'File Processing',
}) => {
    const [selectedInboundOrtfId, setSelectedInboundOrtfId] = useState(
        selectedInboundFile.inboundOrtfId,
    );

    const { data: inboundFiles = [], isFetching: isFetchingFiles } = useGetInboundFilesQuery();
    const { data: overview, isFetching: isFetchingOverview } = useGetInboundFileOverviewQuery(
        selectedInboundOrtfId,
        { skip: !selectedInboundOrtfId },
    );

    const fileOptions = [...inboundFiles]
        .sort((a, b) => sortDateDesc(a.dateReceived, b.dateReceived))
        .map(({ inboundOrtfId, sendingPharmacyName, dateReceived }) => ({
            value: inboundOrtfId,
            name: formatInboundOrtfSourceLabel(sendingPharmacyName, dateReceived) ?? inboundOrtfId,
        }));

    const selectedItem = fileOptions.find(({ value }) => value === selectedInboundOrtfId);
    const selectedFile =
        inboundFiles.find(({ inboundOrtfId }) => inboundOrtfId === selectedInboundOrtfId) ??
        selectedInboundFile;
    const sourceLabel =
        formatInboundOrtfSourceLabel(selectedFile.sendingPharmacyName, selectedFile.dateReceived) ??
        selectedFile.fileName;

    return (
        <div className="padding__around--medium">
            <ActionButtonPageTitle
                label={backLabel}
                onClick={onBack}
                title="Inbound File Details"
            />
            <FullscreenLoading
                isShowing={isFetchingFiles || isFetchingOverview}
                loadingText="Loading..."
            />
            <Formik
                initialValues={{ inboundOrtfId: selectedInboundOrtfId }}
                enableReinitialize
                onSubmit={() => undefined}
            >
                {(formik) => (
                    <div className="margin__top--medium" style={{ maxWidth: '450px' }}>
                        <SuggestSingleSelectFormik
                            id="inboundOrtfId"
                            label="Inbound File"
                            isLoading={isFetchingFiles}
                            values={fileOptions}
                            selectedItem={selectedItem}
                            onChange={(newValue) => {
                                const nextId = newValue?.value ?? '';
                                formik.setFieldValue('inboundOrtfId', nextId);
                                setSelectedInboundOrtfId(nextId);
                            }}
                        />
                    </div>
                )}
            </Formik>
            {overview ? <OrtfInboundFileOverviewContent overview={overview} /> : null}
            {!isFetchingOverview && !overview && selectedInboundOrtfId ? (
                <p className="margin__top--large">{sourceLabel}</p>
            ) : null}
        </div>
    );
};

const inboundFilePropType = PropTypes.shape({
    inboundOrtfId: PropTypes.string.isRequired,
    fileName: PropTypes.string,
    sendingPharmacyName: PropTypes.string,
    dateReceived: PropTypes.string,
});

OrtfInboundFileDetailsView.propTypes = {
    selectedInboundFile: inboundFilePropType.isRequired,
    onBack: PropTypes.func.isRequired,
    backLabel: PropTypes.string,
};
